/**
 * Sentry 错误监控工具库
 * 提供初始化、手动上报、错误边界组件
 */
import * as Sentry from '@sentry/react'

// ============================================================================
// 初始化
// ============================================================================

/**
 * 初始化 Sentry SDK
 * 在应用入口（main.tsx）中调用，必须在 render 前执行
 */
export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN

  if (!dsn) {
    console.warn('[Sentry] 未配置 VITE_SENTRY_DSN，错误监控已禁用')
    return
  }

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE, // 'development' | 'production'
    
    // 性能监控
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
    
    // 采样率配置
    tracesSampleRate: import.meta.env.PROD ? 0.2 : 1.0, // 生产环境采样 20%
    replaysSessionSampleRate: 0.1, // 10% 的会话录制
    replaysOnErrorSampleRate: 1.0, // 出错时 100% 录制
    
    // 忽略常见的非关键错误
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      'Non-Error promise rejection captured',
    ],
  })

  console.log('[Sentry] 错误监控已启用')
}

// ============================================================================
// 手动上报
// ============================================================================

/**
 * 上报上下文类型
 */
interface CaptureContext {
  feature?: string      // 功能模块：copywriting, image-pipeline, export 等
  action?: string       // 具体操作：generate, analyze, upload 等
  [key: string]: unknown // 其他业务上下文
}

/**
 * 手动上报错误到 Sentry
 * 用于 try-catch 块中捕获的业务错误
 * 
 * @param error 错误对象或错误消息
 * @param context 上报上下文（feature, action 等）
 */
export function captureError(
  error: unknown,
  context?: CaptureContext
) {
  // 未初始化时静默跳过
  if (!import.meta.env.VITE_SENTRY_DSN) {
    return
  }

  const errorObj = error instanceof Error ? error : new Error(String(error))
  
  Sentry.withScope((scope) => {
    // 设置标签
    if (context?.feature) {
      scope.setTag('feature', context.feature)
    }
    if (context?.action) {
      scope.setTag('action', context.action)
    }
    
    // 设置额外上下文
    const extras = { ...context }
    delete extras.feature
    delete extras.action
    if (Object.keys(extras).length > 0) {
      scope.setExtras(extras)
    }
    
    Sentry.captureException(errorObj)
  })
}

/**
 * 上报信息级别的消息（非错误）
 * 用于追踪关键业务事件
 */
export function captureMessage(
  message: string,
  context?: CaptureContext
) {
  if (!import.meta.env.VITE_SENTRY_DSN) {
    return
  }

  Sentry.withScope((scope) => {
    if (context?.feature) {
      scope.setTag('feature', context.feature)
    }
    if (context?.action) {
      scope.setTag('action', context.action)
    }
    
    Sentry.captureMessage(message, 'info')
  })
}

// ============================================================================
// 错误边界
// ============================================================================

/**
 * Sentry 错误边界组件
 * 直接导出 Sentry 提供的 ErrorBoundary
 */
export const SentryErrorBoundary = Sentry.ErrorBoundary

/**
 * 错误退回 UI 组件
 * 显示中文友好提示，引导用户刷新页面
 */
export function ErrorFallback() {
  const handleRefresh = () => {
    window.location.reload()
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center p-8 max-w-md">
        <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-red-100 flex items-center justify-center">
          <svg
            className="w-8 h-8 text-red-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>
        <h2 className="text-xl font-semibold text-gray-800 mb-2">
          应用遇到一点小问题
        </h2>
        <p className="text-gray-600 mb-6">
          错误已自动记录，我们会尽快修复。请刷新页面重试。
        </p>
        <button
          onClick={handleRefresh}
          className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
        >
          刷新页面
        </button>
      </div>
    </div>
  )
}








